import React, { useState, useEffect, useMemo, useRef } from "react";
import Dropdown from "../ui/dropdown/Dropdown";
import ExportDropdown from "../ui/dropdown/ExportDropdown";
import CompareBarLineChart from "../ui/charts/CompareBarLineChart";
import useFetch from "../../hooks/useFetch";
import Slider from "../ui/Slider";
import Wrapper from "../helpers/Wrapper";
import PageHeader from "../layout/pageHeader";
import SelectedGlobalFilter from "../ui/SelectedGlobalFilter";
import WrapperCard from "../ui/card/card";
import useApi from "../../hooks/useApi";
import Card from "../ui/Card";
import { createErrorLoaderSection, createLoaderSection, createNoDataLoaderSection } from "../ui/states/States";
import { SwiperSlide } from "swiper/react";
import NoDataAvailableChecker from "../../utils/NoDataAvailableChecker";
import ErrorBoundary from "../../utils/ErrorBoundary";
import Pie from "../ui/charts/Pie";
import BarChart from "../ui/charts/BarChart";
import StackedColumnChart from "../ui/charts/StackedColumnChart";
import Loader from "../ui/loaders/Loader";
import Button from "../ui/Button";
import Datatable from "../ui/tables/Datatable";
import SummaryDatatable from "../ui/tables/SummaryDatatable";
import { getUniqueValuesFromArrofObj } from "../../utils/Utils";
import WaterFallChart from "../ui/charts/WaterFallChart";
import BubbleChartSizeShapeDynamic from "../ui/charts/BubbleChartSizeShapeDynamic";
import CombinedBarLineMultipleAxesChart from "../ui/charts/CombinedBarLineMultipleAxesChart";
import HeatRiskGrowth from "../ui/charts/HeatRiskGrowth";
import { getUniqueXY } from "../../utils/table";
import SimpleTreeMap from "../ui/charts/SimpleTreeMap";
import CustomBarChart from "../ui/charts/CustomBarChart";
import PremiumIndexChart from "../ui/charts/PremiumIndexChart";
import { useSelector } from "react-redux";
import SortOrderButton from "../ui/dropdown/SortOrderButton";
// import { ReactComponent as TableIcon } from "../../../assets/icons/icons-svg/Table-icon.svg";
import { ReactComponent as TableIcon } from "../../assets/icons/icons-svg/Table-icon.svg";

import { ReactComponent as ChartIcon } from "../../assets/icons/icons-svg/Chart-icon.svg";

const filterKeyMap = {
    country: "country_id",
    store_type: "store_type_id",
    category: "category_id",
    sub_category: "sub_category_id",
    segment: "segment_id",
    form: "form_id",
    manufacture: "manufacture_id",
    brand: "brand_id",
    variant: "variant_id",
    pack_size: "pack_size_id",
    promotion: "promotion_id",
    sku: "sku_id",
};



const GrowthCompareAnalysis = ({
    filterKey,
    filterIds,
    dimension,
    selectedOrder = [],
    apiGlobalFilters
}) => {
    const [chart, setChart] = useState({
        data: [],
        x: { label: '', values: [] },
        y: { label: '', values: [] },
    });
    const { post, isLoading, error } = useFetch();
    const api_version = '-v1';

    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
    } = useSelector((state) => state.date);

    const valueDropdown = [
        { label: 'Sales Value', value: 'sales_value' },
        { label: 'Sales Volume', value: 'sales_volume' },
        { label: 'Sales Unit', value: 'sales_unit' },
    ];
    const [valueFilter, setValueFilter] = useState(valueDropdown[0]);

    const allDimensionDropdown = [
        { label: 'Country', value: 'country' },
        { label: 'Store Type', value: 'store_type' },
        { label: 'Category', value: 'category' },
        { label: 'Sub Category', value: 'sub_category' },
        { label: 'Segment', value: 'segment' },
        { label: 'Form', value: 'form' },
        { label: 'Manufacture', value: 'manufacture' },
        { label: 'Brand', value: 'brand' },
        { label: 'Variant', value: 'variant' },
        { label: 'Pack Size', value: 'pack_size' },
        { label: 'Promotion', value: 'promotion' },
        { label: 'SKU', value: 'sku' },
    ];

    const dimensionDropdown = useMemo(
        () => allDimensionDropdown.filter(opt => opt.value !== dimension),
        [dimension]
    );
    const [dimensionFilter, setDimensionFilter] = useState(
        dimensionDropdown[0] || { label: '', value: '' }
    );
    useEffect(() => {
        setDimensionFilter(dimensionDropdown[0] || { label: '', value: '' });
    }, [dimensionDropdown]);
    const sortOrder = 'desc';

    const cleanData = (data) => {
        if (!data) return [];
        return data.map((item) => {
            const cleanedItem = {};
            Object.keys(item).forEach((key) => {
                cleanedItem[key] = item[key] === null || item[key] === undefined ? '' : item[key];
            });
            return cleanedItem;
        });
    };

    const payload = useMemo(
        () => ({
            global_filter: {
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters,
            },
            section_filter: {
                compare_filter: {
                    [`${filterKey}_id`]: filterIds,
                },
                value_dropdown: valueFilter.value,
                dimension_dropdown: dimensionFilter.value,
                dimension_dropdown1: dimension,
                top_bottom: {
                    sort_by: sortOrder,
                    size: 10,
                },
            },
        }),
        [
            startDate,
            endDate,
            prevStartDate,
            prevEndDate,
            secondPrevStartDate,
            secondPrevEndDate,
            valueFilter,
            dimensionFilter,
            apiGlobalFilters,
            sortOrder,
            filterKey,
            filterIds,
            dimension
        ]
    );

    // Fetch chart data
    useEffect(() => {
        const fetchChartData = async () => {
            try {
                const response = await post(`ri/sales/compare-growth-analysis${api_version}`, payload);
                if (response) {
                    const uniqueX = [...new Set(response.map((item) => item.x))];
                    const uniqueY = [...new Set(response.map((item) => item.y))];
                    setChart({
                        data: cleanData(response),
                        x: { label: dimensionFilter.value, values: uniqueX },
                        y: { label: 'brand', values: uniqueY },
                    });
                }
            } catch (err) {
                setChart({ data: [], x: { label: '', values: [] }, y: { label: '', values: [] } });
            }
        };
        fetchChartData();
        // eslint-disable-next-line
    }, [payload]);

    // --- Sort X axis and chart data to match selectedOrder ---
    const sortedXAxisValues = useMemo(() => {
        // Only reorder if selectedOrder is present and all exist in chart.x.values
        if (
            !selectedOrder.length ||
            !chart.x?.values?.length
        ) return chart.x.values;
        // Only keep those from selectedOrder that exist in x.values
        return selectedOrder.filter(label =>
            chart.x.values.includes(label)
        );
    }, [selectedOrder, chart.x]);

    // For a heatmap, you must keep all data, but you want the columns in the selectedOrder.
    // So, you pass sortedXAxisValues to the chart and the chart will show columns in that order.

    // Optionally: filter out data rows for columns not in selectedOrder
    const filteredChartData = useMemo(() => {
        if (!selectedOrder.length) return chart.data;
        return chart.data.filter(d => sortedXAxisValues.includes(d.x));
    }, [chart.data, sortedXAxisValues, selectedOrder]);

    // --------------------------------------------------------

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!filteredChartData.length || NoDataAvailableChecker(filteredChartData))
            return createNoDataLoaderSection('stackedColumnChart');
        return (
            <Wrapper>
                <ErrorBoundary>
                    <HeatRiskGrowth
                        chartId="growthCompareChart"
                        chartClass="chart-container"
                        yAxisLabels={chart.y}
                        xAxisLabels={{ ...chart.x, values: sortedXAxisValues }} // pass reordered x!
                        data={filteredChartData}
                        valueFieldName={valueFilter.value}
                    />
                </ErrorBoundary>
            </Wrapper>
        );
    };

    return (
        <Wrapper>
            <ErrorBoundary>
                <div
                    id="growth_compare_analysis_section"
                    className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card"
                >
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Growth Comparison Analysis</h2>
                            <div data-html2canvas-ignore>
                                <Dropdown
                                    placeholder="Dimension"
                                    options={dimensionDropdown}
                                    className="grid_margin_right compare_dropdown "
                                    value={dimensionFilter}
                                    selectedOptions={dimensionFilter}
                                    setStatedropdown={setDimensionFilter}
                                />
                                <Dropdown
                                    placeholder="Value"
                                    options={valueDropdown}
                                    className="grid_margin_right compare_dropdown"

                                    value={valueFilter}
                                    selectedOptions={valueFilter}
                                    setStatedropdown={setValueFilter}
                                />
                                <ExportDropdown
                                    sectionId="growth_compare_analysis"
                                    sectionName="growth_compare_analysis"
                                    sectionData={filteredChartData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const PriceCompareImpactAnalysis = ({
    filterKey,
    filterIds,
    dimension,
    selectedOrder = [], // <-- pass in the selected dropdown order of labels
    apiGlobalFilters
}) => {
    const api_version = "-v1";
    const { post, isLoading, error } = useFetch();

    // Redux dates (replace with your own date state if needed)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    const [chart, setChart] = useState({ data: [], label: [] });

    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate
            },
            filter: apiGlobalFilters // <-- use here
        },
        section_filter: {
            compare_filter: {
                [`${filterKey}_id`]: filterIds,
            },
            dimension_dropdown: dimension,
        }
    }), [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters, // <-- add here  
        filterKey,
        filterIds,
        dimension
    ]);

    useEffect(() => {
        if (!filterIds || filterIds.length === 0) {
            setChart({ data: [], label: [] });
            return;
        }
        const fetchChartData = async () => {
            try {
                const response = await post(`ri/sales/compare-price-impact-analysis${api_version}`, payload);
                setChart(response);
            } catch {
                setChart({ data: [], label: [] });
            }
        };
        fetchChartData();
        // eslint-disable-next-line
    }, [payload, filterIds]);

    // Sort chart data to match selectedOrder (dropdown order)
    const sortedChartData = useMemo(() => {
        if (!chart.data || !selectedOrder.length) return chart.data;
        // Only show those in selectedOrder, in the same order as selectedOrder
        return selectedOrder
            .map(lbl => chart.data.find(d => d.label === lbl))
            .filter(Boolean);
    }, [chart.data, selectedOrder]);

    // Chart options and loader logic unchanged
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: false,
                grid: { display: false }
            },
            y: {
                stacked: false,
                beginAtZero: true,
                min: 0,
                ticks: {
                    callback: function (value) {
                        return value + '%';
                    }
                },
                grid: {
                    drawBorder: false,
                    color: function (context) {
                        return context.tick.value === 0 ? '#666' : '#eee';
                    },
                    lineWidth: function (context) {
                        return context.tick.value === 0 ? 2 : 1;
                    }
                }
            }
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    boxWidth: 12,
                    padding: 20,
                    font: { size: 12 }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.raw;
                        const formattedValue = Math.abs(value) > 1000
                            ? value.toLocaleString('en-US', { maximumFractionDigits: 2 })
                            : value.toFixed(2);
                        return `${label}: ${formattedValue}%`;
                    }
                },
                displayColors: true,
                backgroundColor: 'rgba(0,0,0,0.8)',
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
                padding: 10
            }
        },
        elements: {
            bar: {
                backgroundColor: function (context) {
                    const value = context.raw;
                    return value < 0
                        ? 'rgba(255, 99, 132, 0.8)'
                        : 'rgba(54, 162, 235, 0.8)';
                },
                borderColor: function (context) {
                    const value = context.raw;
                    return value < 0
                        ? 'rgba(255, 99, 132, 1)'
                        : 'rgba(54, 162, 235, 1)';
                },
                borderWidth: 1,
                borderRadius: 4,
                minBarLength: 2
            }
        }
    };

    const transformChartData = (data) => {
        if (!data || !data.data) return data;
        return {
            ...data,
            data: data.data.map(item => ({
                ...item,
                percentage_change_in_average_price_per_unit: Number(item.percentage_change_in_average_price_per_unit),
                percentage_change_in_sales_value: Number(item.percentage_change_in_sales_value),
                percentage_change_in_sales_unit: Number(item.percentage_change_in_sales_unit)
            }))
        };
    };

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );
    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };

    const chartContent = isLoading ? (
        <Loader loaderType="stackedColumnChartLoader" />
    ) : error ? (
        createErrorLoaderSection("stackedColumnChart")
    ) : !sortedChartData?.length || NoDataAvailableChecker(sortedChartData) ? (
        createNoDataLoaderSection("stackedColumnChart")
    ) : (
        <Wrapper>
            <ErrorBoundary>
                <div style={{ minHeight: 320, height: "340px", position: 'relative' }}>
                    <BarChart
                        chartClass="section-chart-wrapper"
                        chartData={transformChartData({ ...chart, data: sortedChartData }).data}
                        chartLabels={chart.label}
                        chartId={"priceCompareImpactChart"}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                        isHorizontal={false}
                        options={chartOptions}
                        variant="centeredLegend"
                    />
                </div>
            </ErrorBoundary>
        </Wrapper>
    );

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="price_compare_impact_analysis_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Price Impact Analysis</h2>
                            <div data-html2canvas-ignore={true}>
                                <ExportDropdown
                                    sectionId="price_compare_impact_analysis"
                                    sectionName="price_compare_impact_analysis"
                                    sectionData={sortedChartData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {chartContent}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const CompareDistributionEfficiency = ({
    filterKey,
    filterIds,
    dimension,
    granularity = "year",
    value = "sales_value",
    tooltip = "distribution_effectiveness",
    selectedOrder = [], // <-- Pass the dropdown order of labels here
    apiGlobalFilters
}) => {
    const api_version = '-v1';
    const { post, isLoading, error } = useFetch();

    const [chart, setChart] = useState({ chartData: [], chartLabels: [] });

    // Redux dates (replace with your own date state if needed)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate
            },
            filter: apiGlobalFilters // <-- use here
        },
        section_filter: {
            compare_filter: {
                [`${filterKey}_id`]: filterIds,
            },
            dimension_dropdown: dimension,
            tooltip_dropdown: tooltip,
            value_dropdown: value,
            granularity_dropdown: granularity,
        }
    }), [
        startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate,
        filterKey, filterIds, dimension, granularity, value, tooltip, apiGlobalFilters
    ]);

    useEffect(() => {
        if (!filterIds || filterIds.length === 0 || !dimension) {
            setChart({ chartData: [], chartLabels: [] });
            return;
        }
        const fetchChartData = async () => {
            try {
                const response = await post(
                    `ri/sales/compare-distribution-efficiency${api_version}`,
                    payload
                );
                setChart({
                    chartData: response?.data || [],    // <-- map 'data' to 'chartData'
                    chartLabels: response?.label || [], // <-- map 'label' to 'chartLabels'
                });
            } catch {
                setChart({ chartData: [], chartLabels: [] });
            }
        };
        fetchChartData();
        // eslint-disable-next-line
    }, [payload]);

    // ---- SORT chart.chartData to match selectedOrder ----
    const sortedChartData = chart.chartData;
    // ----------------------------------------------------

    const NoDataAvailableChecker = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        return Object.keys(data).length === 0;
    };
    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );
    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );

    const renderContent = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection('stackedColumnChart');
        if (!sortedChartData || sortedChartData.length === 0 || NoDataAvailableChecker(sortedChartData))
            return createNoDataLoaderSection('stackedColumnChart');
        return (
            <ErrorBoundary>
                <div style={{ minHeight: 320, height: "340px", position: 'relative' }}>
                    <BarChart
                        chartClass="section-chart-wrapper"
                        chartData={sortedChartData}
                        chartLabels={chart.chartLabels}
                        chartId={'compareDistributionEfficiencyChart'}
                        showLabels
                        showLegend
                        tooltipMaxWidth="auto"
                        isHorizontal={false}
                        variant="centeredLegend"
                    />
                </div>
            </ErrorBoundary>
        );
    };

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="compare_distribution_efficiency_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Distribution Efficiency</h2>
                            <div data-html2canvas-ignore>
                                <ExportDropdown
                                    sectionId="compare_distribution_efficiency"
                                    sectionName="compare_distribution_efficiency"
                                    sectionData={sortedChartData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};

const compareOptions = [
    { label: 'Select', value: '' },
    { label: 'Country', value: 'country' },
    { label: 'Store Type', value: 'store_type' },
    { label: 'Category', value: 'category' },
    { label: 'Sub Category', value: 'sub_category' },
    { label: 'Segment', value: 'segment' },
    { label: 'Form', value: 'form' },
    { label: 'Manufacture', value: 'manufacture' },
    { label: 'Brand', value: 'brand' },
    { label: 'Variant', value: 'variant' },
    { label: 'Pack Size', value: 'pack_size' },
    { label: 'Promotion', value: 'promotion' },
    { label: 'SKU', value: 'sku' },
];

const staticChartData = [
    [
        { label: "MAT 2Y", sales_value: 0, sales_volume: 0 },
        { label: "MAT LY", sales_value: 0, sales_volume: 0 },
        { label: "MAT TY", sales_value: 0, sales_volume: 0 }
    ],
    [
        { label: "MAT 2Y", sales_value: 0, sales_volume: 0 },
        { label: "MAT LY", sales_value: 0, sales_volume: 0 },
        { label: "MAT TY", sales_value: 0, sales_volume: 0 }
    ],
    [
        { label: "MAT 2Y", sales_value: 0, sales_volume: 0 },
        { label: "MAT LY", sales_value: 0, sales_volume: 0 },
        { label: "MAT TY", sales_value: 0, sales_volume: 0 }
    ]
];

const staticTableData = [
    Array(29).fill("00"),
    Array(29).fill("00"),
    Array(29).fill("00")
];

const defaultDates = {
    mat_ty_start_date: "2023-01-01",
    mat_ty_end_date: "2023-12-08",
    mat_ly_start_date: "2022-01-09",
    mat_ly_end_date: "2022-12-18",
    mat_2y_start_date: "2021-01-09",
    mat_2y_end_date: "2021-12-18"
};

const api_version = "-v1";

function getDynamicTableGroups(compareAnalysisResponses) {
    const first = compareAnalysisResponses.find(slot => slot);
    if (!first) return [];
    const groups = [];
    if (Array.isArray(first.sales) && first.sales.length)
        groups.push({ title: "Sales", rows: first.sales.map(r => r.label) });
    if (Array.isArray(first.distribution) && first.distribution.length)
        groups.push({ title: "Distribution", rows: first.distribution.map(r => r.label) });
    if (Array.isArray(first.top_performance) && first.top_performance.length)
        groups.push({ title: "Top Performer", rows: first.top_performance.map(r => r.label) });
    return groups;
}

const initialManufacturers = [
    { label: "Select", value: "" },
    { label: "Select", value: "" },
    { label: "Select", value: "" }
];

const CompareAnalysis = () => {
    const { post } = useFetch();

    const [selectedCompare, setSelectedCompare] = useState(compareOptions[0]);
    const [globalFilterData, setGlobalFilterData] = useState(null);
    const [selectedManufacturers, setSelectedManufacturers] = useState(initialManufacturers);
    const [compareAnalysisResponses, setCompareAnalysisResponses] = useState([null, null, null]);

    // Reset dropdowns and table on compare option change
    useEffect(() => {
        if (selectedCompare.label === "Select") {
            setSelectedManufacturers([
                { label: "Select", value: "" },
                { label: "Select", value: "" },
                { label: "Select", value: "" }
            ]);
        } else {
            setSelectedManufacturers([
                { label: `Select ${selectedCompare.label}`, value: "" },
                { label: `Select ${selectedCompare.label}`, value: "" },
                { label: `Select ${selectedCompare.label}`, value: "" }
            ]);
        }
        setCompareAnalysisResponses([null, null, null]);
    }, [selectedCompare]);

    // Prepare dropdown options for manufacturers/brands/categories
    const compareDropdownOptions = useMemo(() => {
        const filterKey = selectedCompare.value;
        if (selectedCompare.label === "Select") {
            return [{ label: "Select", value: "" }];
        }
        if (!globalFilterData || !globalFilterData[filterKey] || !Array.isArray(globalFilterData[filterKey]?.data)) {
            return [{ label: `Select ${selectedCompare.label}`, value: "" }];
        }
        const activeItems = globalFilterData[filterKey].data.filter(b => b.is_active);
        return [
            { label: `Select ${selectedCompare.label}`, value: "" },
            ...activeItems.map(b => ({
                label: b.name,
                value: String(b.value ?? "")
            }))
        ];
    }, [globalFilterData, selectedCompare]);

    // Filtered dropdown options for each manufacturer dropdown
    const getFilteredDropdownOptions = (idx) => {
        const selectedValues = selectedManufacturers
            .map((item, i) => (i !== idx ? item.value : null))
            .filter(Boolean);
        return compareDropdownOptions.filter(
            (opt) => !selectedValues.includes(opt.value) || opt.value === ""
        );
    };


    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);

    const [showGlobalFilterAlert, setShowGlobalFilterAlert] = useState(false);
    const prevGlobalFilters = useRef(null);
    useEffect(() => {
        const filtersString = JSON.stringify(selectedFilters);
        if (prevGlobalFilters.current !== null && prevGlobalFilters.current !== filtersString) {
            setShowGlobalFilterAlert(true);
            // RESET dropdowns and manufacturers when global filter changes
            setSelectedCompare(compareOptions[0]);
            setSelectedManufacturers(initialManufacturers);
            setCompareAnalysisResponses([null, null, null]);
        }
        prevGlobalFilters.current = filtersString;
    }, [selectedFilters]);

    const apiGlobalFilters = React.useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Fetch dropdown options (global-filter) when compare changes
    // useEffect(() => {
    //     const fetchGlobalFilter = async () => {
    //         try {
    //             const response = await post("ri/global-filter", {
    //                 filter_input: {
    //                     date_filter: {
    //                         mat_ty_start_date: "2020-01-01",
    //                         mat_ty_end_date: "2022-12-31"
    //                     },
    //                     section_name: [selectedCompare.value]
    //                 }
    //             });
    //             setGlobalFilterData(response);
    //         } catch {
    //             setGlobalFilterData(null);
    //         }
    //     };
    //     fetchGlobalFilter();
    //     // eslint-disable-next-line
    // }, [selectedCompare]);

    useEffect(() => {
        const fetchGlobalFilter = async () => {
            try {
                const response = await post("ri/global-filter", {
                    filter_input: {
                        date_filter: {
                            mat_ty_start_date: startDate,
                            mat_ty_end_date: endDate,
                            mat_ly_start_date: prevStartDate,
                            mat_ly_end_date: prevEndDate,
                            mat_2y_start_date: secondPrevStartDate,
                            mat_2y_end_date: secondPrevEndDate,
                        },
                        section_name: [selectedCompare.value],
                        filter: apiGlobalFilters
                    }
                });
                setGlobalFilterData(response);
            } catch {
                setGlobalFilterData(null);
            }
        };
        fetchGlobalFilter();
        // eslint-disable-next-line
    }, [selectedCompare, apiGlobalFilters, startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate]);

    // New handler for individual dropdown changes
    const handleManufacturerChange = (item, idx) => {
        setSelectedManufacturers(prev => {
            const next = [...prev];
            next[idx] = item;
            return next;
        });

        if (item.value) {
            const filterKey = `${selectedCompare.value}_id`;
            const payload = {
                global_filter: {
                    date_filter: {
                        mat_ty_start_date: startDate,
                        mat_ty_end_date: endDate,
                        mat_ly_start_date: prevStartDate,
                        mat_ly_end_date: prevEndDate,
                        mat_2y_start_date: secondPrevStartDate,
                        mat_2y_end_date: secondPrevEndDate
                    },
                    filter: apiGlobalFilters
                },
                section_filter: {
                    compare_filter: { [filterKey]: [Number(item.value)] },
                    top_bottom: { sort_by: "desc", size: 1 }
                }
            };
            post(`ri/sales/compare-analysis${api_version}`, payload)
                .then(response => {
                    setCompareAnalysisResponses(prev => {
                        const next = [...prev];
                        next[idx] = response;
                        return next;
                    });
                })
                .catch(() => {
                    setCompareAnalysisResponses(prev => {
                        const next = [...prev];
                        next[idx] = null;
                        return next;
                    });
                });
        } else {
            setCompareAnalysisResponses(prev => {
                const next = [...prev];
                next[idx] = null;
                return next;
            });
        }
    };

    // Table groups from API or fallback to static
    // const tableGroups = useMemo(() => {
    //     const dynamic = getDynamicTableGroups(compareAnalysisResponses);
    //     if (dynamic.length > 0) return dynamic;
    //     return [
    //         {
    //             title: "Sales",
    //             rows: [
    //                 "Sales Value", "Sales Volume", "Sales Unit", "Market Share by Sales Value",
    //                 "Market Share by Sales Volume", "Market Share by Sales Unit", "Promotional Sales Value %",
    //                 "Promotional Sales Volume %", "Promotional Sales Unit %", "Rate of Sale by Value",
    //                 "Rate of Sale by Volume", "Rate of Sale by Units", "Average Price Sensity Index"
    //             ]
    //         },
    //         {
    //             title: "Distribution",
    //             rows: [
    //                 "Sales Value Per Point of Distribution", "Sales Volume Per Point of Distribution",
    //                 "Sales Units Per Point of Distribution", "Weighted Distribution Points (TDP)",
    //                 "Numeric Distribution Selling", "Distribution Effectiveness(M)"
    //             ]
    //         },
    //         {
    //             title: "Top Performer",
    //             rows: [
    //                 "Country", "Store Type", "Category", "Sub Category", "Segment", "Form",
    //                 "Manufacture", "Brand", "Variant", "Pack Size"
    //             ]
    //         }
    //     ];
    // }, [compareAnalysisResponses]);
    const tableGroups = useMemo(() => {
        // Always show all static sections when "Select" is chosen
        if (selectedCompare.label === "Select") {
            return [
                {
                    title: "Sales",
                    rows: [
                        "Sales Value", "Sales Volume", "Sales Unit", "Market Share By Sales Value %",
                        "Market Share By Sales Volume %", "Market Share By Sales Unit %", "Promotional Sales Value %",
                        "Promotional Sales Volume %", "Promotional Sales Unit %", "Rate of Sale By Value",
                        "Rate of Sale By Volume", "Rate of Sale By Units", "Average Price Sensity Index"
                    ]
                },
                {
                    title: "Distribution",
                    rows: [
                        "Sales Value Per Point of Distribution", "Sales Volume Per Point of Distribution",
                        "Sales Units Per Point of Distribution", "Weighted Distribution Points (TDP)",
                        "Numeric Distribution Selling", "Distribution Effectiveness(M)"
                    ]
                },
                {
                    title: "Top Performer",
                    rows: [
                        "Country", "Store Type", "Category", "Sub Category", "Segment", "Form",
                        "Manufacture", "Brand", "Variant", "Pack Size"
                    ]
                }
            ];
        }
        // Otherwise, use dynamic groups from API, or fallback to static if API returns nothing
        const dynamic = getDynamicTableGroups(compareAnalysisResponses);
        if (dynamic.length > 0) return dynamic;
        return [
            {
                title: "Sales",
                rows: [
                    "Sales Value", "Sales Volume", "Sales Unit", "Market Share By Sales Value %",
                    "Market Share By Sales Volume %", "Market Share By Sales Unit %", "Promotional Sales Value %",
                    "Promotional Sales Volume %", "Promotional Sales Unit %", "Rate of Sale By Value",
                    "Rate of Sale By Volume", "Rate of Sale By Units", "Average Price Sensity Index"
                ]
            },
            {
                title: "Distribution",
                rows: [
                    "Sales Value Per Point of Distribution", "Sales Volume Per Point of Distribution",
                    "Sales Units Per Point of Distribution", "Weighted Distribution Points (TDP)",
                    "Numeric Distribution Selling", "Distribution Effectiveness(M)"
                ]
            },
            {
                title: "Top Performer",
                rows: [
                    "Country", "Store Type", "Category", "Sub Category", "Segment", "Form",
                    "Manufacture", "Brand", "Variant", "Pack Size"
                ]
            }
        ];
    }, [compareAnalysisResponses, selectedCompare.label]);
    // For footer (last group/row index)
    const lastGroupIdx = tableGroups.length - 1;
    const lastGroup = tableGroups[lastGroupIdx];
    const lastRowIdx = lastGroup.rows.length - 1;

    // Helper to get value for a cell
    function getSlotValue(slotResponse, group, label) {
        if (!slotResponse) return "-";
        let groupKey = group.title.toLowerCase().replace(/\s/g, "_");
        if (groupKey.startsWith("top_performer")) groupKey = "top_performance";
        if (groupKey.startsWith("distribution")) groupKey = "distribution";
        if (!slotResponse[groupKey]) return "-";

        if (groupKey === "sales" || groupKey === "distribution") {
            const found = slotResponse[groupKey].find(item =>
                item.label.trim().toLowerCase() === label.trim().toLowerCase()
            );
            return found && found.value !== undefined && found.value !== ""
                ? (typeof found.value === "number"
                    ? Number(found.value).toLocaleString(undefined, { maximumFractionDigits: 3 })
                    : found.value)
                : "-";
        }
        if (groupKey === "top_performance") {
            const found = slotResponse[groupKey].find(item =>
                item.label.trim().toLowerCase() === label.trim().toLowerCase()
            );
            return found && found.value !== undefined && found.value !== "" ? found.value : "-";
        }
        return "-";
    }

    // Helper for staticTableData fallback
    // function renderTableValue(val) {
    //     return val !== "" && val !== undefined && val !== null ? val : "-";
    // }
    function renderTableValue(val, group) {
        // If group is Top Performer, always show "-"
        if (group && group.title === "Top Performer") return "-";
        // For other sections, keep "00" as fallback
        return val !== "" && val !== undefined && val !== null ? val : "00";
    }
    // ----------- EXPORT DATA HELPER -----------
    function buildExportTableData() {
        const headerRow = [
            selectedCompare.label,
            ...selectedManufacturers.map(m => m.label)
        ];
        const rows = [headerRow];

        tableGroups.forEach(group => {
            rows.push([group.title, '', '', '']);
            group.rows.forEach(label => {
                const row = [label];
                for (let colIdx = 0; colIdx < 3; colIdx++) {
                    if (selectedManufacturers[colIdx]?.value && compareAnalysisResponses[colIdx]) {
                        row.push(getSlotValue(compareAnalysisResponses[colIdx], group, label));
                    } else {
                        // row.push(renderTableValue(staticTableData[colIdx][0]));
                        row.push(renderTableValue(staticTableData[colIdx][0], group));
                    }
                }
                rows.push(row);
            });
        });
        return rows;
    }
    // ------------------------------------------

    return (
        <ErrorBoundary>
            <div className="section_card compare-analysis-section">
                <div className="section_card_header compare-analysis-header-row">
                    {/* <div className="compare-analysis-title">Compare Analysis</div> */}
                    <div className="section_card_title">Compare Analysis</div>

                    <div className="compare-analysis-header-controls">
                        <Dropdown
                            placeholder="Compare By"
                            options={compareOptions}
                            value={selectedCompare}
                            selectedOptions={selectedCompare}
                            setStatedropdown={setSelectedCompare}
                            className="compare-by-dropdown"
                        />
                        <ExportDropdown
                            sectionId="compare_analysis_section"
                            sectionName="Compare Analysis"
                            sectionData={buildExportTableData()}
                        />
                    </div>
                </div>
                {showGlobalFilterAlert && (
                    <div className="compare-analysis-alert-wrapper">
                        <div className="compare-analysis-alert">
                            <span className="compare-alert-icon">
                                <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                    <circle cx="10" cy="10" r="9" fill="#e3f6ff" stroke="#1e90ff" strokeWidth="1.5" />
                                    <g>
                                        <path d="M10 6.5v4" stroke="#1e90ff" strokeWidth="1.5" strokeLinecap="round" />
                                        <circle cx="10" cy="13.1" r="1" fill="#1e90ff" />
                                    </g>
                                </svg>
                            </span>
                            <span className="compare-alert-text">
                                <strong>Info:</strong>
                                <span style={{ marginLeft: 10 }}>
                                    This section contains data derived from the latest global filter inputs.
                                </span>
                            </span>
                            <button
                                className="compare-alert-close"
                                onClick={() => setShowGlobalFilterAlert(false)}
                                aria-label="Dismiss"
                                type="button"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
                <div id="compare_analysis_section" className="compare-analysis-table-scroll">
                    <table className="compare-analysis-table">
                        <colgroup>
                            <col className="compare-analysis-col-label" />
                            <col className="compare-analysis-col-slot" />
                            <col className="compare-analysis-col-slot" />
                            <col className="compare-analysis-col-slot" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th></th>
                                {[0, 1, 2].map(idx => (
                                    <th key={idx}>
                                        <Dropdown
                                            placeholder={`Select ${selectedCompare.label}`}
                                            options={getFilteredDropdownOptions(idx)}
                                            value={selectedManufacturers[idx]}
                                            selectedOptions={selectedManufacturers[idx]}
                                            setStatedropdown={item => handleManufacturerChange(item, idx)}
                                            className="compare-analysis-dropdown"
                                        />
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th></th>
                                {[0, 1, 2].map(idx => (
                                    <th key={idx}>
                                        <CompareBarLineChart
                                            chartId={`compareChart-${idx}`}
                                            chartClass="compare-bar-line-chart"
                                            chartData={
                                                (selectedManufacturers[idx]?.value && compareAnalysisResponses[idx]?.line_chart_data?.data && compareAnalysisResponses[idx]?.line_chart_data?.data.length > 0)
                                                    ? compareAnalysisResponses[idx].line_chart_data.data
                                                    : staticChartData[idx]
                                            }
                                            chartLabels={
                                                (selectedManufacturers[idx]?.value && compareAnalysisResponses[idx]?.line_chart_data?.dimension)
                                                    ? compareAnalysisResponses[idx].line_chart_data.dimension
                                                    : []
                                            }
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableGroups.map((group, groupIdx) => (
                                <React.Fragment key={group.title}>
                                    <tr>
                                        <td colSpan={4} className="compare-analysis-section-row" style={{
                                            fontSize: 16,
                                            fontWeight: 700,
                                            fontFamily: 'Ubuntu, sans-serif'
                                        }}>{group.title}</td>
                                    </tr>
                                    {group.rows.map((label, rowIdx) => {
                                        const flatIdx = tableGroups
                                            .slice(0, groupIdx)
                                            .reduce((acc, g) => acc + g.rows.length, 0) + rowIdx;
                                        const isLastRow = groupIdx === tableGroups.length - 1 && rowIdx === tableGroups[tableGroups.length - 1].rows.length - 1;
                                        return (
                                            <React.Fragment key={label}>
                                                <tr>
                                                    <td className="compare-analysis-label" style={{
                                                        fontSize: 14,
                                                        fontWeight: 400,
                                                        fontFamily: 'Ubuntu, sans-serif'
                                                    }}>{label}</td>
                                                    {[0, 1, 2].map(colIdx => (
                                                        <td className="compare-analysis-value" key={colIdx} style={{
                                                            fontSize: 14,
                                                            fontWeight: 400,
                                                            fontFamily: 'Ubuntu, sans-serif'
                                                        }} >
                                                            {(selectedManufacturers[colIdx]?.value && compareAnalysisResponses[colIdx])
                                                                ? getSlotValue(compareAnalysisResponses[colIdx], group, label)
                                                                // : renderTableValue(staticTableData[colIdx][flatIdx])}
                                                                : renderTableValue(staticTableData[colIdx][flatIdx], group)}
                                                        </td>
                                                    ))}
                                                </tr>
                                                {isLastRow && selectedCompare.value && selectedManufacturers.some(m => m.value) && (
                                                    <tr>
                                                        <td></td>
                                                        <td colSpan={3} style={{ padding: 0, background: "transparent" }}>
                                                            {/* <div className="section_card market-share-growth-card" style={{ margin: "24px 0" }}> */}
                                                            <div className="market-share-growth-card" style={{ margin: "24px 0" }}>

                                                                <GrowthCompareAnalysis
                                                                    filterKey={selectedCompare.value}
                                                                    filterIds={selectedManufacturers.map(m => Number(m.value)).filter(Boolean)}
                                                                    dimension={selectedCompare.value}
                                                                    selectedOrder={selectedManufacturers.map(m => m.label)}
                                                                    apiGlobalFilters={apiGlobalFilters}
                                                                />
                                                            </div>
                                                            {/* <div className="section_card price-impact-analysis-card market-share-growth-card" style={{ margin: "24px 0" }}> */}
                                                            <div className="market-share-growth-card" style={{ margin: "24px 0" }}>

                                                                <PriceCompareImpactAnalysis
                                                                    filterKey={selectedCompare.value}
                                                                    filterIds={selectedManufacturers.map(item => Number(item.value)).filter(Boolean)}
                                                                    dimension={selectedCompare.value}
                                                                    selectedOrder={selectedManufacturers.map(item => item.label)}
                                                                    apiGlobalFilters={apiGlobalFilters}
                                                                />
                                                            </div>
                                                            <div className="market-share-growth-card" style={{ margin: "24px 0" }}>
                                                                <CompareDistributionEfficiency
                                                                    filterKey={selectedCompare.value}
                                                                    filterIds={selectedManufacturers.map(m => Number(m.value)).filter(Boolean)}
                                                                    dimension={selectedCompare.value}
                                                                    selectedOrder={selectedManufacturers.map(m => m.label)}
                                                                    apiGlobalFilters={apiGlobalFilters}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default CompareAnalysis;